import type {
  Coding,
  Observation,
  Patient,
  Practitioner,
  Quantity,
  Reference,
} from '@medplum/fhirtypes'
import { formatClinicalUnit } from '#src/display.ts'
import { unitLabel } from '#src/models/observationComponents.ts'
import {
  applyCalculationMethod,
  type DerivationRule,
  observationCategory,
} from '#src/observation-logic/derivations/rules.ts'
import { interpretScore } from '#src/observation-logic/derivations/scores.ts'
import {
  bandSetFor,
  bandToInterpretation,
} from '#src/observation-logic/interpretation.ts'
import {
  numberValue,
  type ObservationValue,
} from '#src/observation-logic/value.ts'
import { OBSERVATION_CALCULATION_EXTENSION_URL } from '#src/structureDefinitions.ts'
import { now } from '#src/utils.ts'

// Identifies the one record a computed node writes, by the node's id, so
// two editors on the same note create it once.
export const DERIVED_OBSERVATION_IDENTIFIER_SYSTEM = 'urn:seen:derived'

// The conditional-create query for a node's computed record. Node ids are
// copied with a note template, so the identifier alone would find another
// participant's record; the search is scoped to the participant and skips
// records entered in error.
export function derivedObservationQuery(
  nodeId: string,
  participant: Reference<Patient>
): string {
  return `identifier=${DERIVED_OBSERVATION_IDENTIFIER_SYSTEM}|${nodeId}&patient=${participant.reference}&status:not=entered-in-error`
}

const UCUM = 'http://unitsofmeasure.org'

export interface DerivedObservationFields {
  valueQuantity?: Quantity
  derivedFrom?: Observation['derivedFrom']
  method?: Observation['method']
  extension?: Observation['extension']
  interpretation?: Observation['interpretation']
}

function quantityOf(value: ObservationValue | null): Quantity | undefined {
  if (value?.kind !== 'number') {
    return undefined
  }
  if (!value.unit) {
    return { value: value.value }
  }
  const label = unitLabel(value.unit)
  return {
    value: value.value,
    unit: label === value.unit ? formatClinicalUnit(value.unit) : label,
    system: UCUM,
    code: value.unit,
  }
}

// The calculation extension part naming the cutoff set a value was banded
// by, so a reader knows which published bands its interpretation came from.
const CUTOFFS_PART = 'cutoffs'

// What a computed record carries besides its code and subject: the value
// (none when the inputs are gone), its inputs, its method and calculation,
// and its band where the code has bands, by the chosen or default cutoffs.
export function derivedObservationFields({
  rule,
  value,
  target,
  derivedFrom,
  bandSetId,
  existingExtensions = [],
}: {
  rule: DerivationRule
  value: ObservationValue | null
  target: Pick<Coding, 'system' | 'code'>
  derivedFrom: NonNullable<Observation['derivedFrom']>
  bandSetId?: string | null
  existingExtensions?: Observation['extension']
}): DerivedObservationFields {
  const bandSet = bandSetFor(target, bandSetId)
  const withMethod = applyCalculationMethod(
    {
      resourceType: 'Observation',
      status: 'final',
      code: {},
      extension: existingExtensions.filter(
        (extension) => extension.url !== OBSERVATION_CALCULATION_EXTENSION_URL
      ),
    },
    rule
  )
  const band =
    value?.kind === 'number' && bandSet
      ? interpretScore(value.value, bandSet.bands)
      : undefined
  return {
    valueQuantity: quantityOf(value),
    derivedFrom,
    method: withMethod.method,
    extension: bandSet
      ? withCutoffs(withMethod.extension, bandSet.id)
      : withMethod.extension,
    interpretation: band ? [bandToInterpretation(band)] : undefined,
  }
}

function withCutoffs(
  extensions: Observation['extension'],
  bandSetId: string
): Observation['extension'] {
  return (extensions ?? []).map((extension) =>
    extension.url === OBSERVATION_CALCULATION_EXTENSION_URL
      ? {
          ...extension,
          extension: [
            ...(extension.extension ?? []).filter(
              (part) => part.url !== CUTOFFS_PART
            ),
            { url: CUTOFFS_PART, valueString: bandSetId },
          ],
        }
      : extension
  )
}

// A new computed record for a node.
export function buildDerivedObservation({
  rule,
  value,
  target,
  derivedFrom,
  bandSetId,
  nodeId,
  participant,
  practitioner,
  status,
  effectiveDateTime = now(),
}: {
  rule: DerivationRule
  value: ObservationValue | null
  target: Coding
  derivedFrom: NonNullable<Observation['derivedFrom']>
  bandSetId?: string | null
  nodeId: string
  participant: Reference<Patient> | undefined
  practitioner: Reference<Practitioner>
  status: 'preliminary' | 'final'
  // When it was taken: the encounter's start when the note has one.
  effectiveDateTime?: string
}): Observation {
  const fields = derivedObservationFields({
    rule,
    value,
    target,
    derivedFrom,
    bandSetId,
  })
  const observation: Observation = {
    resourceType: 'Observation',
    status,
    identifier: [
      { system: DERIVED_OBSERVATION_IDENTIFIER_SYSTEM, value: nodeId },
    ],
    code: {
      coding: [target],
    },
    subject: participant,
    effectiveDateTime,
    performer: [practitioner],
  }
  if (rule.category) {
    observation.category = observationCategory(rule.category)
  }
  if (target.display) {
    observation.code.text = target.display
  }
  if (fields.valueQuantity) {
    observation.valueQuantity = fields.valueQuantity
  }
  if (derivedFrom.length) {
    observation.derivedFrom = derivedFrom
  }
  if (fields.method) {
    observation.method = fields.method
  }
  if (fields.extension?.length) {
    observation.extension = fields.extension
  }
  if (fields.interpretation) {
    observation.interpretation = fields.interpretation
  }
  return observation
}

// What a computed record holds that a recalculation can change: the value,
// the rule that computed it (its calculation's library id), the cutoffs it
// was banded by, and the records it was read from. Equal keys mean the
// record needs no rewrite.
export function derivationKey({
  value,
  ruleId,
  cutoffsId,
  derivedFrom,
}: {
  value: ObservationValue | null
  ruleId: string | undefined
  cutoffsId?: string
  derivedFrom: Observation['derivedFrom']
}): string {
  return JSON.stringify([
    value?.kind === 'number' ? [value.value, value.unit ?? ''] : null,
    ruleId ?? '',
    cutoffsId ?? '',
    (derivedFrom ?? []).flatMap((source) =>
      source.reference ? [source.reference] : []
    ),
  ])
}

// The key of what a rule computes now (see `derivationKey`).
export function computedDerivationKey({
  rule,
  value,
  derivedFrom,
  bandSetId,
}: {
  rule: DerivationRule
  value: ObservationValue | null
  derivedFrom: Observation['derivedFrom']
  bandSetId?: string | null
}): string {
  return derivationKey({
    value,
    ruleId: rule.method ? rule.id : undefined,
    cutoffsId: rule.method ? bandSetFor(rule.target, bandSetId)?.id : undefined,
    derivedFrom,
  })
}

// The key of what a computed record holds (see `derivationKey`).
export function recordedDerivationKey(record: Observation): string {
  const quantity = record.valueQuantity
  const unit = quantity?.code ?? quantity?.unit
  const parts = record.extension?.find(
    (extension) => extension.url === OBSERVATION_CALCULATION_EXTENSION_URL
  )?.extension
  const ruleId = parts?.find((part) => part.url === 'library')?.valueString
  const cutoffsId = parts?.find(
    (part) => part.url === CUTOFFS_PART
  )?.valueString
  return derivationKey({
    value:
      quantity?.value === undefined ? null : numberValue(quantity.value, unit),
    ruleId,
    cutoffsId,
    derivedFrom: record.derivedFrom,
  })
}
